/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, action } from 'mobx';
import { observer, inject } from 'mobx-react';

import {
  catalogueFilterNames,
  catalogueFilterColorMap,
  getCatalogueFilterColor,
  productMatchesFilter,
} from './catalogueFilterUtils';

// expected props
// - selectedFilter (via prop) — current filter name
// - onSelectedFilter (via prop) — callback when a button is clicked
// - filteredCount (via prop) — optional search-filtered count for the active filter
// - catalogueProductsStore (via injection)
// - envTypesStore (via injection)
class CatalogueFilterButtons extends React.Component {
  get selectedFilter() {
    return this.props.selectedFilter;
  }

  get onSelectedFilter() {
    const fn = this.props.onSelectedFilter;
    return _.isFunction(fn) ? fn : () => {};
  }

  handleSelected = name =>
    action(() => {
      this.onSelectedFilter(name);
    });

  getCounts() {
    const catalogueStore = this.props.catalogueProductsStore;
    const envTypesStore = this.props.envTypesStore;
    if (!catalogueStore) return {};

    const products = catalogueStore.list || [];
    const counts = {};
    _.forEach(_.keys(catalogueFilterColorMap), filterName => {
      counts[filterName] = _.filter(products, p => productMatchesFilter(filterName, p, envTypesStore)).length;
    });
    return counts;
  }

  render() {
    const selectedFilter = this.selectedFilter;
    const counts = this.getCounts();
    const filteredCount = this.props.filteredCount;

    const getButtonStyle = name => {
      const colors = getCatalogueFilterColor(name);
      const isSelected = name === selectedFilter;
      return {
        backgroundColor: isSelected ? colors.activeBg : colors.bg,
        color: isSelected ? '#ffffff' : colors.color,
      };
    };

    return (
      <div className="workspace-filter-container">
        {_.map(_.keys(catalogueFilterColorMap), name => {
          const isSelected = name === selectedFilter;
          const totalForFilter = counts[name] || 0;

          let countLabel;
          if (isSelected && filteredCount !== undefined && filteredCount !== totalForFilter) {
            countLabel = `${filteredCount}/${totalForFilter}`;
          } else {
            countLabel = `${totalForFilter}`;
          }

          return (
            <button
              key={name}
              className={`workspace-filter-btn ${isSelected ? 'selected' : ''}`}
              style={getButtonStyle(name)}
              onClick={!isSelected ? this.handleSelected(name) : undefined}
            >
              {_.startCase(name)}
              <span
                style={{
                  marginLeft: '6px',
                  fontSize: '0.85em',
                  opacity: isSelected ? 0.9 : 0.7,
                }}
              >
                {countLabel}
              </span>
            </button>
          );
        })}
      </div>
    );
  }
}

decorate(CatalogueFilterButtons, {
  selectedFilter: computed,
  onSelectedFilter: computed,
});

export default inject('catalogueProductsStore', 'envTypesStore')(observer(CatalogueFilterButtons));
